// src/components/WeChatLoginButton.tsx
import React, { useState } from 'react';
import { FaWeixin, FaTimes, FaCopy } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const WECHAT_LOGIN_URL = process.env.REACT_APP_WECHAT_LOGIN_URL || '/wechat/login';

const detectEnv = () => {
  if (typeof navigator === 'undefined') {
    return { isMobile: false, isWeChat: false, isDesktop: true };
  }
  const ua = navigator.userAgent.toLowerCase();
  const isWeChat = ua.includes('micromessenger');
  const isMobile = /iphone|ipad|ipod|android|mobile/.test(ua);
  const isDesktop = !isMobile;
  return { isMobile, isWeChat, isDesktop };
};

const WeChatLoginButton: React.FC = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleWeChatLogin = () => {
    const { isDesktop } = detectEnv();
    
    // 如果是 PC 桌面端，跳转到后台调用微信开放平台(qrconnect)登录
    if (isDesktop) {
      window.location.href = WECHAT_LOGIN_URL;
      return;
    }

    // 手机外部浏览器 或 微信内部：均显示引导弹窗（包含长按识别二维码指令）
    // 理由：目前用户仅配置了开放平台密钥，在微信内直接跳转授权会报 10005 权限错误
    setShowModal(true);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.origin).then(() => {
      toast.success("链接已复制，请前往微信粘贴打开！");
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleWeChatLogin}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-green-500 text-green-600 hover:bg-green-50 transition font-semibold"
      >
        <FaWeixin className="text-xl" />
        {t('auth.login_with_wechat') || '使用微信登录'}
      </button>

      {/* 微信公众号引导弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden relative animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="bg-green-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-bold text-lg">
                <FaWeixin className="text-2xl" />
                <span>微信快捷登录</span>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-full transition"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col items-center">
              <p className="text-gray-700 text-sm text-center font-medium mb-4 leading-relaxed">
                请用手机微信扫一扫关注我们，<br/>
                手机用户请长按保存，并在微信内使用扫一扫功能
              </p>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mb-5 shadow-inner">
                <img 
                  src="/images/wechat_mp_qr.jpg" 
                  alt="微信公众号二维码" 
                  className="w-48 h-48 object-contain rounded-lg mix-blend-multiply"
                />
              </div>

              <div className="w-full space-y-3">
                <button
                  onClick={copyLink}
                  className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg font-semibold transition"
                >
                  <FaCopy /> 复制网址到微信中打开
                </button>
                <div className="text-xs text-gray-400 text-center px-4">
                  ※ 手机端请长按二维码保存或识别，或通过文件传输助手在微信中打开链接。
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeChatLoginButton;
